<template>
    <CqModal :showModal="show" :preventClosing="isSaving" @closeModal="closeModal" heading="Voorstel accepteren of weigeren">
        <div v-if="proposal && resource">
            <InfoGrid>
                <InfoHeader>Medewerker</InfoHeader><div>{{ resource.name }}</div>
                <InfoHeader>Datum</InfoHeader><div>{{ formattedDate }}</div>
                <InfoHeader>Rooster</InfoHeader><div>{{ roster?.label ?? 'onbekend' }}</div>
                <InfoHeader>Dienstcode</InfoHeader><div>{{ shiftCode }}</div>
                <InfoHeader>Toestand</InfoHeader><div>{{ organisation?.organisationDeploymentStates[proposal.stateId].caption ?? '' }}</div>
                <template v-if="proposal.publicComment !== null">
                    <InfoHeader>Opmerking</InfoHeader><div>{{ proposal.publicComment }}</div>
                </template>
            </InfoGrid>
            <div class="specifications" :style="{'--column-count': specificationColumnCount}" v-if="proposal.specified">
                <div class="specifications-pretext" v-if="proposal.specifications!.length === 0">De dienst is gespecificeerd als lege dienst.</div>
                <template v-else>
                    <div class="specifications-pretext">Specificaties</div>
                    <div class="specification-header">Van</div>
                    <div class="specification-header">Tot</div>
                    <div class="specification-header">Toestand</div>
                    <div class="specification-header">Type</div>
                    <div class="specification-header" v-if="hasLeave">Verlofregeling</div>
                    <div class="specification-header" v-if="hasProjectOrActivity">Project</div>
                    <div class="specification-header" v-if="hasProjectOrActivity">Activiteit</div>
                </template>
                <template v-for="specification of proposal.specifications!" :key="specification.id!">
                    <div>{{ formatSpecificationTime(specification.begin) }}</div>
                    <div>{{ formatSpecificationTime(specification.end) }}</div>
                    <div>{{ organisation?.organisationDeploymentStates[specification.stateId].caption ?? '' }}</div>
                    <div>{{ organisation?.organisationDeploymentTypes[specification.typeId].name }}</div>
                    <div v-if="hasLeave">{{ formatLeave(specification.leaveKindId) }}</div>
                    <div v-if="hasProjectOrActivity">{{ formatProject(specification.projectId) }}</div>
                    <div v-if="hasProjectOrActivity">{{ formatActivity(specification.activityId) }}</div>
                </template>
            </div>
        </div>
        <CqPlaceholderText v-else :words="100" :lines="10" />
        <CqButtonBar>
            <CqButton label="Annuleren" @click="closeModal" :disabled="isSaving" />
            <CqButton dangerous label="Weigeren" @click="refuse" :disabled="isSaving" />
            <CqButton primary label="Accepteren" @click="accept" :disabled="isSaving" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {CqButton, CqButtonBar, CqModal, CqPlaceholderText} from '@cquential/ui';
    import {ApiClient, DeploymentProposal, Shift, Organisation, Resource, LeaveKind, Project, ApiError, HttpStatusCode} from '@cquential/api';
    import Notify from '@/components/modals/Notify.vue';
    import Dates from '@cquential/date-utils';
    import {RosterSelectOption} from '@/types/types';
    import InfoGrid from '@/components/Branding/InfoGrid.vue';
    import InfoHeader from '@/components/Branding/InfoHeader.vue';

    @Component({
        components: {
            InfoGrid,
            InfoHeader,
            CqPlaceholderText,
            CqModal,
            CqButtonBar,
            CqButton,
        },
        emits: ['updateLocal:deploymentProposal'],
    })
    export default class DeploymentProposalAcceptModal extends Vue {
        private static instance: DeploymentProposalAcceptModal | null;

        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public organisations!: Record<string, Organisation>;
        @Prop({required: true}) public rosters!: RosterSelectOption[];
        @Prop({required: true}) public leaveKinds!: LeaveKind[];
        @Prop() public projects?: Project[];

        public show: boolean = false;
        public isSaving: boolean = false;
        public proposal?: DeploymentProposal = undefined;
        public resource?: Resource = undefined;

        public static openModal(proposal: DeploymentProposal, resource: Resource): void {
            this.instance!.show = true;
            this.instance!.proposal = proposal;
            this.instance!.resource = resource;
        }

        public mounted(): void {
            DeploymentProposalAcceptModal.instance = this;
        }

        public beforeUnmount(): void {
            DeploymentProposalAcceptModal.instance = null;
        }

        public closeModal(): void {
            this.show = false;
        }

        public refuse(): void {
            if (this.isSaving) {
                return;
            }

            this.startUpdate();

            ApiClient.instance.refuseDeploymentProposal(this.proposal!.id!)
                .then((): void => {
                    const updatedProposal: DeploymentProposal = {
                        ...this.proposal!,
                        refused: true,
                    };

                    this.$emit('updateLocal:deploymentProposal', updatedProposal);

                    this.closeModal();
                })
                .catch((): void => {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'Er is iets fout gegaan. Ververs het planbord.', type: 'error'});
                })
                .finally((): void => {
                    this.isSaving = false;
                });
        }

        public accept(): void {
            if (this.isSaving) {
                return;
            }

            this.startUpdate();

            ApiClient.instance.acceptDeploymentProposal(this.proposal!.id!)
                .then((deploymentId: number): void => {
                    const updatedProposal: DeploymentProposal = {
                        ...this.proposal!,
                        deploymentId,
                    };
                    this.$emit('updateLocal:deploymentProposal', updatedProposal);

                    this.closeModal();
                })
                .catch((error: ApiError): void => {
                    if (error.response && error.response.rawResponse.status === HttpStatusCode.FailedDependency) {
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: 'De dienst die aangemaakt zou worden door de acceptatie is niet geldig.', type: 'warning', showDuration: 7000});
                    } else {
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: 'Er is iets fout gegaan. Ververs het planbord.', type: 'error'});
                    }
                })
                .finally((): void => {
                    this.isSaving = false;
                });
        }

        private startUpdate(): void {
            this.isSaving = true;

            const updatedProposal: DeploymentProposal = {
                ...this.proposal!,
                updating: true,
            };
            this.$emit('updateLocal:deploymentProposal', updatedProposal);
        }

        public get formattedDate(): string {
            return this.proposal ? Dates.Formatter.formatDateLong(this.proposal.date) : '';
        }

        public get shiftCode(): string {
            return this.proposal ? this.shift!.template!.code : '';
        }

        public get shift(): Shift | null {
            return this.proposal ? this.shifts[this.proposal.shiftId] : null;
        }

        public get roster(): RosterSelectOption | null {
            return this.shift
                ? (this.rosters.find((roster: RosterSelectOption): boolean => roster.data.id === this.shift!.rosterId) ?? null)
                : null;
        }

        public get organisation(): Organisation | null {
            return this.roster
                ? this.organisations[this.roster.data.organisationId]
                : null;
        }

        public get hasProjectOrActivity(): boolean {
            if (!this.proposal || !this.proposal.specified) {
                return false;
            }

            for (const specification of this.proposal.specifications!) {
                if (specification.activityId || specification.projectId) {
                    return true;
                }
            }

            return false;
        }

        public get hasLeave(): boolean {
            if (!this.proposal || !this.proposal.specified) {
                return false;
            }

            for (const specification of this.proposal.specifications!) {
                if (specification.leaveKindId) {
                    return true;
                }
            }

            return false;
        }

        public get specificationColumnCount(): number {
            return 4 + (this.hasProjectOrActivity ? 2 : 0) + (this.hasLeave ? 1 : 0);
        }

        public get leaveKindNames(): Record<number, string> {
            const result: Record<number, string> = {};
            for (const leaveKind of this.leaveKinds) {
                result[leaveKind.id] = `${leaveKind.code} ${leaveKind.description}`;
            }

            return result;
        }

        public get projectNames(): Record<number, string> {
            if (this.projects === undefined) {
                return {};
            }

            const result: Record<number, string> = {};
            for (const project of this.projects) {
                result[project.id] = `${project.value} - ${project.description}`;
            }

            return result;
        }

        public get activityNames(): Record<number, string> {
            if (this.projects === undefined) {
                return {};
            }

            const result: Record<number, string> = {};
            for (const project of this.projects) {
                if (project.activities) {
                    for (const activity of project.activities) {
                        result[activity.id] = `${activity.value} - ${activity.description}`;
                    }
                }
            }

            return result;
        }

        public formatSpecificationTime(date: Date): string {
            let addition: string = '';
            if (Dates.Formatter.formatDateComputerFriendly(date) > Dates.Formatter.formatDateComputerFriendly(this.proposal!.date)) {
                addition += ' (+1)';
            }

            return Dates.Formatter.formatTime(date) + addition;
        }

        public formatLeave(id: number | null): string {
            if (id === null) {
                return '-';
            }

            return this.leaveKindNames[id] ?? 'onbekend';
        }

        public formatProject(id: number | null): string {
            if (id === null) {
                return '-';
            }

            return this.projectNames[id] ?? 'onbekend';
        }

        public formatActivity(id: number | null): string {
            if (id === null) {
                return '-';
            }

            return this.activityNames[id] ?? 'onbekend';
        }
    }
</script>

<style lang="scss" scoped>
    .specifications {
        background: var(--main-background-color);
        display: grid;
        grid-template-columns: repeat(var(--column-count), auto);
        gap: 0.25rem 1rem;
        border-radius: 0.25rem;
        margin-top: 1rem;
        padding: 1rem;

        .specifications-pretext {
            font-weight: 500;
            grid-column: 1 / -1;
        }
    }
</style>
